import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, CheckCircle2, MapPin, AlertTriangle, RefreshCw } from 'lucide-react';
import { api } from '../../../lib/auth';
import { Time24Input } from '../../ui/time24-input';
import { useI18nStore } from '../../../lib/i18n';
const tt = (k, p) => useI18nStore.getState().t(`startStop.${k}`, p);

export const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const mapsUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

export function useGeo() {
  const [geo, setGeo] = useState({ status: 'idle', coords: null, error: null });
  const request = useCallback(() => {
    if (!navigator.geolocation) {
      setGeo({ status: 'error', coords: null, error: tt('geoUnsupported') });
      return;
    }
    setGeo((g) => ({ ...g, status: 'loading', retried: g.status === 'error' || !!g.retried }));
    navigator.geolocation.getCurrentPosition(
      (p) => setGeo({ status: 'ok', retried: false, coords: { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }, error: null }),
      (e) => setGeo({
        status: 'error', coords: null, retried: false, denied: e.code === 1,
        error: e.code === 1 ? tt('geoDenied') : tt('geoFailed'),
      }),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  }, []);
  return [geo, request];
}

export function GeoBox({ geo, onRetry }) {
  if (geo.status === 'ok') {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800" data-testid="ss-geo-ok">
        <MapPin className="h-5 w-5 shrink-0" />
        <span className="flex-1">{tt('geoOk')} <span className="text-xs opacity-70">(±{geo.coords.accuracy} m)</span></span>
        <button type="button" onClick={onRetry} className="p-1" title={tt('refresh')}><RefreshCw className="h-4 w-4" /></button>
      </div>
    );
  }
  if (geo.status === 'error') {
    return (
      <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800 space-y-2" data-testid="ss-geo-error">
        <p className="flex items-start gap-2"><AlertTriangle className="h-5 w-5 shrink-0" />{geo.error}</p>
        <button type="button" onClick={onRetry} className="w-full rounded-lg bg-rose-600 text-white py-2 font-semibold" data-testid="ss-geo-retry">{tt('geoRetry')}</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-xl bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-800" data-testid="ss-geo-loading">
      <Loader2 className="h-5 w-5 animate-spin" />{tt('geoLoading')}
    </div>
  );
}

export async function uploadAttachment(assignmentId, file) {
  const fd = new FormData();
  fd.append('file', file, file.name || 'foto.jpg');
  const r = await api.post(`/planning/assignments/${assignmentId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return r.data?.data;
}

export function BigField({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export const bigInput = 'w-full h-14 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 text-2xl font-bold tabular-nums focus:border-sky-500 focus:outline-none';

export function KmInput({ assignmentId, value, onChange, photo, onPhoto, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try { onPhoto(await uploadAttachment(assignmentId, f)); toast.success(tt('kmPhotoUploaded')); }
    catch (err) { toast.error(err?.response?.data?.detail || tt('photoUploadFailed')); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2">
      <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder={tt('kmPlaceholder')} value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 7))} className={bigInput} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-photo-btn`}
        className={`h-14 w-16 shrink-0 rounded-xl border-2 flex items-center justify-center overflow-hidden ${photo ? 'border-emerald-500' : 'border-slate-300 dark:border-slate-600'}`} title={tt('kmPhotoTitle')}>
        {busy ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          : photo?.cdn_url ? <img src={photo.cdn_url} alt="km" className="h-full w-full object-cover" />
            : photo ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <Camera className="h-6 w-6 text-slate-500" />}
      </button>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
    </div>
  );
}

export function Stepper({ value, onChange, testId, maxDigits = 4 }) {
  const n = parseInt(value || '0', 10) || 0;
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={() => onChange(String(Math.max(0, n - 1)))} className="h-14 w-14 rounded-xl bg-slate-100 dark:bg-slate-800 text-2xl font-bold" data-testid={`${testId}-minus`}>−</button>
      <input type="text" inputMode="numeric" pattern="[0-9]*" value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, maxDigits))}
        className={`${bigInput} text-center`} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => onChange(String(n + 1))} className="h-14 w-14 rounded-xl bg-slate-100 dark:bg-slate-800 text-2xl font-bold" data-testid={`${testId}-plus`}>+</button>
    </div>
  );
}

export function TimeInput({ value, onChange, testId }) {
  return (
    <div className="flex gap-2">
      <Time24Input value={value} onChange={onChange} className={bigInput} testId={`${testId}-input`} />
      <button type="button" onClick={() => onChange(nowHHMM())} className="h-14 px-4 rounded-xl border-2 border-sky-300 text-sky-700 font-semibold" data-testid={`${testId}-now`}>{tt('now')}</button>
    </div>
  );
}
